/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { WorkerRecord } from '@karya/core';
import { authApi } from '../../../service/api/auth/Api';
import { getErrorMessage } from '../../../utils/error';
import { getWorkerDisplayName } from '../../../utils/worker';

export type LoginStep = 'PHONE' | 'OTP' | 'LOGGED_IN';

const RESEND_OTP_COOLDOWN_SECONDS = 30;

type UseOtpLoginOptions = {
  onLoginSuccess?: (worker: WorkerRecord) => void;
};

export const useOtpLogin = ({ onLoginSuccess }: UseOtpLoginOptions = {}) => {
  const [step, setStep] = useState<LoginStep>('PHONE');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otpInput, setOtpInput] = useState('');
  const [otpId, setOtpId] = useState<string | null>(null);
  const [activeWorker, setActiveWorker] = useState<WorkerRecord | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [cooldownEndTime, setCooldownEndTime] = useState<number | null>(null);
  const [lastOtpPhoneNumber, setLastOtpPhoneNumber] = useState<string | null>(null);
  const [requestPhoneNumber, setRequestPhoneNumber] = useState<string | null>(null);

  const [
    generateOtp,
    {
      data: generateData,
      isLoading: isGenerating,
      isSuccess: isGenerateSuccess,
      isError: isGenerateError,
      error: generateError,
    },
  ] = authApi.useGenerateOTPMutation();
  const [
    verifyOtp,
    {
      data: verifyData,
      isLoading: isVerifying,
      isSuccess: isVerifySuccess,
      isError: isVerifyError,
      error: verifyError,
    },
  ] = authApi.useVerifyOTPMutation();
  const [
    resendOtp,
    { isLoading: isResending, isSuccess: isResendSuccess, isError: isResendError, error: resendError },
  ] = authApi.useResendOTPMutation();

  const handlePhoneNumberChange = useCallback(
    (value: string) => {
      const digits = value.replace(/\D/g, '').slice(0, 10);
      setPhoneNumber(digits);
      if (phoneError) {
        setPhoneError(null);
      }
      // Clear request phone number marker to ignore in-flight responses for different phone
      if (digits !== requestPhoneNumber) {
        setRequestPhoneNumber(null);
      }
      // Only reset step and OTP state if phone number is different from last OTP phone number
      // This prevents unnecessary resets when user re-enters the same number
      if (step !== 'PHONE' && digits !== lastOtpPhoneNumber) {
        setStep('PHONE');
        setOtpInput('');
        setOtpId(null);
        setOtpError(null);
      }
    },
    [phoneError, step, lastOtpPhoneNumber, requestPhoneNumber],
  );

  const handleGenerateOtp = useCallback(() => {
    if (phoneNumber.length !== 10) {
      setPhoneError('Please enter a valid 10-digit phone number.');
      return;
    }
    if (phoneError) {
      setPhoneError(null);
    }
    setRequestPhoneNumber(phoneNumber);
    void generateOtp({ phone_number: phoneNumber });
  }, [generateOtp, phoneError, phoneNumber]);

  const handleVerifyOtp = useCallback(() => {
    if (otpInput.length !== 6 || !otpId) {
      setOtpError('Please enter a valid 6-digit OTP.');
      return;
    }
    if (otpError) {
      setOtpError(null);
    }
    void verifyOtp({
      phone_number: phoneNumber,
      otp_id: otpId,
      otp: otpInput,
    });
  }, [otpError, otpId, otpInput, phoneNumber, verifyOtp]);

  const handleResendOtp = useCallback(() => {
    if (!otpId || !phoneNumber) {
      return;
    }

    const now = Date.now();
    const remainingTime = cooldownEndTime ? Math.max(0, Math.ceil((cooldownEndTime - now) / 1000)) : 0;

    // If same phone number and timer is still running, don't make a new request
    if (phoneNumber === lastOtpPhoneNumber && remainingTime > 0) {
      return;
    }

    // Make the resend request
    void resendOtp({
      phone_number: phoneNumber,
      otp_id: otpId,
    });
  }, [otpId, phoneNumber, lastOtpPhoneNumber, cooldownEndTime, resendOtp]);

  useEffect(() => {
    if (!isGenerateSuccess || !generateData) return;
    // Only apply state updates if the response matches the request phone number
    // This prevents stale OTP state from being resurrected when phoneNumber changes
    if (phoneNumber === requestPhoneNumber || phoneNumber === lastOtpPhoneNumber) {
      setOtpId(generateData.otp_id);
      setStep('OTP');
      setOtpError(null);
      setLastOtpPhoneNumber(phoneNumber);
      setCooldownEndTime(Date.now() + RESEND_OTP_COOLDOWN_SECONDS * 1000);
      setRequestPhoneNumber(null);
    }
  }, [generateData, isGenerateSuccess, phoneNumber, requestPhoneNumber, lastOtpPhoneNumber]);

  useEffect(() => {
    if (!isVerifySuccess) return;
    if (!verifyData || verifyData.length === 0) {
      setOtpError('No worker records found.');
      return;
    }
    setActiveWorker(verifyData[0]);
    setStep('LOGGED_IN');
    setOtpError(null);
    onLoginSuccess?.(verifyData[0]);
  }, [isVerifySuccess, onLoginSuccess, verifyData]);

  useEffect(() => {
    if (isGenerateError && generateError) {
      const errorMessage = getErrorMessage(generateError);
      setPhoneError(errorMessage);
    }
  }, [generateError, isGenerateError]);

  useEffect(() => {
    if (isVerifyError && verifyError) {
      const errorMessage = getErrorMessage(verifyError);
      setOtpError(errorMessage);
    }
  }, [isVerifyError, verifyError]);

  useEffect(() => {
    if (isResendSuccess) {
      setCooldownEndTime(Date.now() + RESEND_OTP_COOLDOWN_SECONDS * 1000);
      setLastOtpPhoneNumber(phoneNumber);
      setOtpError(null);
    }
  }, [isResendSuccess, phoneNumber]);

  useEffect(() => {
    if (isResendError && resendError) {
      const errorMessage = getErrorMessage(resendError);
      setOtpError(errorMessage);
    }
  }, [isResendError, resendError]);

  // Reset timer when phone number changes
  useEffect(() => {
    if (phoneNumber !== lastOtpPhoneNumber) {
      setCooldownEndTime(null);
    }
  }, [phoneNumber, lastOtpPhoneNumber]);

  // Update cooldown display every second when timer is active
  const [resendCooldown, setResendCooldown] = useState(0);
  useEffect(() => {
    if (!cooldownEndTime) {
      setResendCooldown(0);
      return;
    }

    const updateCooldown = () => {
      const now = Date.now();
      const remaining = Math.max(0, Math.ceil((cooldownEndTime - now) / 1000));

      if (remaining <= 0) {
        setCooldownEndTime(null);
        setResendCooldown(0);
        return;
      }

      setResendCooldown(remaining);
    };

    // Update immediately
    updateCooldown();

    const interval = setInterval(updateCooldown, 1000);

    return () => clearInterval(interval);
  }, [cooldownEndTime]);

  const isLoading = isGenerating || isVerifying || isResending;
  const canResendOtp = resendCooldown === 0 && otpId !== null && step === 'OTP';
  const workerDisplayName = useMemo(() => (activeWorker ? getWorkerDisplayName(activeWorker) : null), [activeWorker]);

  return {
    step,
    phoneNumber,
    otpInput,
    phoneError,
    otpError,
    activeWorker,
    workerDisplayName,
    isLoading,
    resendCooldown,
    canResendOtp,
    setOtpInput,
    handlePhoneNumberChange,
    handleGenerateOtp,
    handleVerifyOtp,
    handleResendOtp,
  };
};
